import React from 'react';
import { Mail } from 'lucide-react';
import { resolveLogoFullId } from '../../lib/platformLogo';
import { normalizeWhatsAppNumber, buildWaMeLink } from '../../lib/whatsapp';
import { XIcon, InstagramIcon, SnapchatIcon, TikTokIcon, YouTubeIcon, WhatsAppIcon } from '../icons/SocialIcons';

const FILES_BASE = process.env.REACT_APP_BACKEND_URL;

export const LandingFooter = ({ platformSettings, landing, theme }) => {
  const logoFileId = resolveLogoFullId(platformSettings, theme);
  const fullLogoUrl = logoFileId ? `${FILES_BASE}/api/files/${logoFileId}` : null;
  const showSaudiBadge = Boolean(platformSettings.saudi_badge_enabled && platformSettings.saudi_badge_logo_file_id);
  const saudiBadgeUrl = showSaudiBadge ? `${FILES_BASE}/api/files/${platformSettings.saudi_badge_logo_file_id}` : null;
  const whatsappNumber = normalizeWhatsAppNumber(platformSettings.contact_phone);
  const socials = [
    { url: platformSettings.twitter_url, icon: XIcon, label: 'X / تويتر' },
    { url: platformSettings.instagram_url, icon: InstagramIcon, label: 'إنستغرام' },
    { url: platformSettings.snapchat_url, icon: SnapchatIcon, label: 'سناب شات' },
    { url: platformSettings.tiktok_url, icon: TikTokIcon, label: 'تيك توك' },
    { url: platformSettings.youtube_url, icon: YouTubeIcon, label: 'يوتيوب' },
  ].filter((s) => s.url);

  return (
    <footer id="contact" className="border-t border-teal-900/20 bg-[#040a12] py-16" data-testid="landing-footer">
      <div className="mx-auto max-w-7xl px-5">
        <div className="grid grid-cols-1 gap-10 sm:grid-cols-3">
          <div className="text-start">
            {fullLogoUrl ? (
              <img src={fullLogoUrl} alt={platformSettings.platform_name || 'تسيار'} className="h-8 w-auto object-contain drop-shadow-[0_1px_6px_rgba(255,255,255,0.25)]" />
            ) : (
              <span className="font-display text-2xl font-black tracking-tight">
                <span className="text-white">تس</span><span className="text-teal-400">يار</span>
              </span>
            )}
            <p className="mt-4 max-w-xs text-sm leading-relaxed text-slate-400" data-testid="landing-footer-description">
              {landing?.footer_description || 'منصة متكاملة لإدارة الأندية والأكاديميات الرياضية، مع عزل بيانات كامل لكل منشأة.'}
            </p>
          </div>

          <div className="text-start">
            <h4 className="font-display text-sm font-bold text-white">روابط سريعة</h4>
            <ul className="mt-4 space-y-2.5 text-sm text-slate-400">
              <li><a href="#features" className="hover:text-white">الميزات</a></li>
              <li><a href="#how-it-works" className="hover:text-white">كيف تعمل؟</a></li>
              <li><a href="#testimonials" className="hover:text-white">آراء العملاء</a></li>
              <li><a href="/updates" className="hover:text-white" data-testid="landing-footer-updates-link">آخر التحديثات</a></li>
            </ul>
          </div>

          <div className="text-start">
            <h4 className="font-display text-sm font-bold text-white">{landing?.contact_title || 'تواصل معنا'}</h4>
            <div className="mt-4 space-y-3 text-sm text-slate-400">
              {platformSettings.contact_email && (
                <a href={`mailto:${platformSettings.contact_email}`} className="flex items-center gap-2 transition-colors hover:text-teal-300" data-testid="landing-footer-email-link">
                  <Mail className="h-4 w-4 text-teal-400" /><span dir="ltr">{platformSettings.contact_email}</span>
                </a>
              )}
              {platformSettings.contact_phone && (
                whatsappNumber ? (
                  <a href={buildWaMeLink(whatsappNumber, '')} target="_blank" rel="noreferrer" className="flex items-center gap-2 transition-colors hover:text-teal-300" data-testid="landing-footer-phone-link">
                    <WhatsAppIcon className="h-4 w-4 text-teal-400" /><span>تواصل عبر واتساب</span>
                  </a>
                ) : (
                  <a href={`tel:${platformSettings.contact_phone}`} className="flex items-center gap-2 transition-colors hover:text-teal-300" data-testid="landing-footer-phone-link">
                    <WhatsAppIcon className="h-4 w-4 text-teal-400" /><span dir="ltr">{platformSettings.contact_phone}</span>
                  </a>
                )
              )}
            </div>
            <a href="/contact" className="mt-4 inline-block rounded-full border border-teal-500/40 px-4 py-1.5 text-xs font-bold text-teal-300 transition-colors hover:border-teal-400 hover:text-teal-200" data-testid="landing-footer-contact-form-link">
              راسلنا عبر النموذج
            </a>
            {socials.length > 0 && (
              <div className="mt-5 flex gap-3">
                {socials.map((s, i) => (
                  <a key={i} href={s.url} target="_blank" rel="noreferrer" title={s.label} className="flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 bg-white/[0.03] text-slate-400 transition-colors hover:border-teal-500/50 hover:text-teal-400" data-testid={`landing-social-${i}`}>
                    <s.icon className="h-4 w-4" />
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="mt-12 flex flex-col items-center gap-4 border-t border-white/5 pt-8 text-center text-xs text-slate-500 sm:flex-row sm:justify-center sm:gap-8">
          <span>© {new Date().getFullYear()} {platformSettings.platform_name || 'تسيار'}. جميع الحقوق محفوظة.</span>
          <div className="flex items-center gap-4">
            <a href="/privacy" className="hover:text-slate-300" data-testid="landing-footer-privacy-link">سياسة الخصوصية</a>
            <span className="h-1 w-1 rounded-full bg-slate-600" />
            <a href="/terms" className="hover:text-slate-300" data-testid="landing-footer-terms-link">الشروط والأحكام</a>
          </div>
        </div>
        {showSaudiBadge && (
          <div className="mt-6 flex flex-col items-center justify-center gap-2 border-t border-white/5 pt-6 text-center sm:flex-row sm:gap-3" data-testid="landing-footer-saudi-badge">
            <img src={saudiBadgeUrl} alt="رؤية السعودية 2030" className="h-7 w-auto object-contain opacity-90" />
            <span className="text-xs text-slate-500">صُنع بكل حب في المملكة العربية السعودية</span>
          </div>
        )}
      </div>
    </footer>
  );
};

export default LandingFooter;
